import { cn } from '@/lib/utils';
import { FileText, LucideIcon, MapPin, Package, QrCode, Shield, Truck } from 'lucide-react';
import { MouseEvent } from 'react';
import { ShipmentProgressLineProps, ShipmentProgressLineVariant, ShipmentProgressStageKey } from './types';

interface ShipmentProgressStage {
  icon: LucideIcon;
  label: string;
  stageKey: ShipmentProgressStageKey;
}

const shipmentProgressButtonStates = {
  completedActive: {
    connectorAfter: 'success',
    state: 'active',
    type: 'completed',
    variant: 'outlined'
  },
  completedDefault: {
    connectorAfter: 'success',
    state: 'default',
    type: 'completed',
    variant: 'flat'
  },
  disabledDefault: {
    connectorAfter: 'muted',
    state: 'default',
    type: 'disabled',
    variant: 'flat'
  },
  progressActive: {
    connectorAfter: 'muted',
    state: 'active',
    type: 'progress',
    variant: 'outlined'
  },
  progressDefault: {
    connectorAfter: 'muted',
    state: 'default',
    type: 'progress',
    variant: 'flat'
  }
} as const;

const defaultShipmentProgressStages: ShipmentProgressStage[] = [
  {
    icon: FileText,
    label: 'Оформление',
    stageKey: 'preparation'
  },
  {
    icon: Package,
    label: 'Приемка',
    stageKey: 'reception'
  },
  {
    icon: QrCode,
    label: 'Маркировка',
    stageKey: 'marking'
  },
  {
    icon: Truck,
    label: 'Международная логистика',
    stageKey: 'transit'
  },
  {
    icon: Shield,
    label: 'Таможня',
    stageKey: 'customs'
  },
  {
    icon: MapPin,
    label: 'Внутренняя логистика',
    stageKey: 'delivery'
  }
];

const shipmentsListProgressStages: ShipmentProgressStage[] = [
  {
    icon: FileText,
    label: 'Оформление',
    stageKey: 'preparation'
  },
  {
    icon: Package,
    label: 'Приемка',
    stageKey: 'reception'
  },
  {
    icon: QrCode,
    label: 'Маркировка',
    stageKey: 'marking'
  },
  {
    icon: Truck,
    label: 'Международная логистика',
    stageKey: 'transit'
  },
  {
    icon: Shield,
    label: 'Таможня',
    stageKey: 'customs'
  },
  {
    icon: MapPin,
    label: 'Внутренняя логистика',
    stageKey: 'delivery'
  }
];

const interactiveStageKeysByVariant: Record<ShipmentProgressLineVariant, ShipmentProgressStageKey[]> = {
  default: [
    'customs',
    'delivery',
    'marking',
    'preparation',
    'reception',
    'transit'
  ],
  shipmentsList: []
};

const shipmentProgressStagesByVariant: Record<ShipmentProgressLineVariant, ShipmentProgressStage[]> = {
  default: defaultShipmentProgressStages,
  shipmentsList: shipmentsListProgressStages
};

const tileClassByTypeByVariant: Record<ShipmentProgressLineVariant, Record<keyof typeof shipmentProgressButtonStates, string>> = {
  default: {
    completedActive:
      'bg-[hsl(var(--success))] text-[hsl(var(--brand-dark))] shadow-[0_1px_2px_0_rgba(110,231,183,0.1),0_1px_3px_0_rgba(110,231,183,0.1)]',
    completedDefault:
      'bg-[hsl(var(--success))] text-[hsl(var(--brand-dark))] shadow-[0_1px_2px_0_rgba(110,231,183,0.1),0_1px_3px_0_rgba(110,231,183,0.1)]',
    disabledDefault:
      'bg-[hsl(var(--state-disabled-bg)/0.06)] text-muted-foreground-soft shadow-[0_1px_2px_0_rgba(19,16,13,0.1),0_1px_3px_0_rgba(19,16,13,0.1)]',
    progressActive:
      'bg-accent text-on-accent shadow-[0_1px_2px_0_rgba(106,128,255,0.1),0_1px_3px_0_rgba(106,128,255,0.1)]',
    progressDefault:
      'bg-accent text-on-accent shadow-[0_1px_2px_0_rgba(106,128,255,0.1),0_1px_3px_0_rgba(106,128,255,0.1)]'
  },
  shipmentsList: {
    completedActive:
      'bg-[hsl(var(--success))] text-[hsl(var(--brand-dark))] shadow-[0_0.667px_0.667px_rgba(110,231,183,0.1),0_0.667px_1px_rgba(110,231,183,0.1)]',
    completedDefault:
      'bg-[hsl(var(--success))] text-[hsl(var(--brand-dark))] shadow-[0_0.667px_0.667px_rgba(110,231,183,0.1),0_0.667px_1px_rgba(110,231,183,0.1)]',
    disabledDefault:
      'bg-[hsl(var(--state-disabled-bg)/0.05)] text-muted-foreground shadow-[0_0.667px_1.333px_0_rgba(19,16,13,0.1),0_0.667px_2px_0_rgba(19,16,13,0.1)]',
    progressActive:
      'bg-accent text-on-accent shadow-[0_0.667px_0.667px_rgba(106,128,255,0.1),0_0.667px_1px_rgba(106,128,255,0.1)]',
    progressDefault:
      'bg-accent text-on-accent shadow-[0_0.667px_0.667px_rgba(106,128,255,0.1),0_0.667px_1px_rgba(106,128,255,0.1)]'
  }
};

const tileClassByType = {
  completed:
    'bg-[hsl(var(--success))] text-[hsl(var(--brand-dark))] shadow-[0_1px_2px_0_rgba(110,231,183,0.1),0_1px_3px_0_rgba(110,231,183,0.1)]',
  disabled:
    'bg-[hsl(var(--state-disabled-bg)/0.06)] text-muted-foreground-soft shadow-[0_1px_2px_0_rgba(19,16,13,0.1),0_1px_3px_0_rgba(19,16,13,0.1)]',
  progress:
    'bg-accent text-on-accent shadow-[0_1px_2px_0_rgba(106,128,255,0.1),0_1px_3px_0_rgba(106,128,255,0.1)]'
} as const;

const connectorClassByTone = {
  muted: 'bg-[hsl(var(--muted-foreground-soft))]',
  success: 'bg-[hsl(var(--success-soft))]'
} as const;

const connectorClassByToneByVariant: Record<
  ShipmentProgressLineVariant,
  Record<(typeof shipmentProgressButtonStates)[keyof typeof shipmentProgressButtonStates]['connectorAfter'], string>
> = {
  default: connectorClassByTone,
  shipmentsList: {
    muted: 'bg-[hsl(var(--muted-foreground))]',
    success: 'bg-[hsl(var(--success-soft))]'
  }
};

const frameClassByType = {
  completed:
    'bg-[hsl(var(--success))] shadow-[0_1px_2px_0_rgba(110,231,183,0.1),0_1px_3px_0_rgba(110,231,183,0.1)]',
  progress:
    'bg-accent shadow-[0_1px_2px_0_rgba(106,128,255,0.1),0_1px_3px_0_rgba(106,128,255,0.1)]'
} as const;

const buttonBaseClassName =
  'shrink-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

const flatTileClassNameByVariant: Record<ShipmentProgressLineVariant, string> = {
  default: 'flex items-center justify-center px-[12px] py-[8px] rounded-[8px]',
  shipmentsList: 'flex items-center justify-center px-[8px] py-[5.333px] rounded-[5.333px]'
};

const iconClassNameByVariant: Record<ShipmentProgressLineVariant, string> = {
  default: 'size-5',
  shipmentsList: 'size-[13.333px]'
};

const itemClassNameByVariant: Record<ShipmentProgressLineVariant, string> = {
  default: 'flex items-center gap-[2px]',
  shipmentsList: 'flex items-center gap-[1.333px]'
};

const connectorBaseClassNameByVariant: Record<ShipmentProgressLineVariant, string> = {
  default: 'h-[2px] min-w-[1px] flex-1 rounded-full',
  shipmentsList: 'h-[1.333px] min-w-[1px] flex-1'
};

const outlinedButtonClassName = 'flex shrink-0 flex-col items-start p-[2px] rounded-[8px] transition-colors';

const outlinedTileClassName =
  'flex w-full items-center justify-center px-[10px] py-[6px] rounded-[6px] border border-solid border-[hsl(var(--surface))] shadow-none';

const getStageButtonClassName = (
  isStageInteractive: boolean,
  isOutlined: boolean,
  type: keyof typeof tileClassByType
): string => {
  const stateClassName = isOutlined && type !== 'disabled'
    ? cn(outlinedButtonClassName, frameClassByType[type])
    : buttonBaseClassName;

  return cn(
    stateClassName,
    'disabled:cursor-default',
    isStageInteractive && 'cursor-pointer'
  );
};

const getDefaultStageStateKey = (
  activeStageKey: ShipmentProgressStageKey,
  stageKey: ShipmentProgressStageKey,
  stages: ShipmentProgressStage[]
): keyof typeof shipmentProgressButtonStates => {
  const activeStageIndex = stages.findIndex((stage) => stage.stageKey === activeStageKey);
  const enabledStageKeys = interactiveStageKeysByVariant.default;
  const isEnabled = enabledStageKeys.includes(stageKey);
  const stageIndex = stages.findIndex((stage) => stage.stageKey === stageKey);

  if (!isEnabled) return 'disabledDefault';

  if (stageKey === activeStageKey) return stageKey === 'preparation' ? 'completedActive' : 'progressActive';

  if (stageIndex < activeStageIndex) return 'completedDefault';

  return 'disabledDefault';
};

const getShipmentsListStageStateKey = (
  activeStageKey: ShipmentProgressStageKey,
  stageKey: ShipmentProgressStageKey,
  stages: ShipmentProgressStage[]
): keyof typeof shipmentProgressButtonStates => {
  const activeStageIndex = stages.findIndex((stage) => stage.stageKey === activeStageKey);
  const stageIndex = stages.findIndex((stage) => stage.stageKey === stageKey);

  if (stageKey === activeStageKey) return stageKey === 'preparation' ? 'completedActive' : 'progressActive';

  if (stageIndex < activeStageIndex) return 'completedDefault';

  return 'disabledDefault';
};

const getStageStateKey = (
  activeStageKey: ShipmentProgressStageKey,
  stageKey: ShipmentProgressStageKey,
  stages: ShipmentProgressStage[],
  variant: ShipmentProgressLineVariant
): keyof typeof shipmentProgressButtonStates => {
  if (variant === 'shipmentsList') {
    return getShipmentsListStageStateKey(activeStageKey, stageKey, stages);
  }

  return getDefaultStageStateKey(activeStageKey, stageKey, stages);
};

const getStageTileClassName = (
  isOutlined: boolean,
  stateKey: keyof typeof shipmentProgressButtonStates,
  variant: ShipmentProgressLineVariant
): string => {
  return cn(
    isOutlined ? outlinedTileClassName : flatTileClassNameByVariant[variant],
    isOutlined
      ? tileClassByType[shipmentProgressButtonStates[stateKey].type]
      : tileClassByTypeByVariant[variant][stateKey]
  );
};

const ShipmentProgressLine = (props: ShipmentProgressLineProps) => {
  const {
    activeStageKey = 'reception',
    className,
    isInteractive = false,
    onSelectStage,
    selectedStageKey = activeStageKey,
    variant = 'default'
  } = props;

  const stages = shipmentProgressStagesByVariant[variant];
  const interactiveStageKeys = interactiveStageKeysByVariant[variant];

  const onClickStageButton = (
    event: MouseEvent<HTMLButtonElement>,
    stageKey: ShipmentProgressStageKey
  ): void => {
    event.stopPropagation();
    onSelectStage?.(stageKey);
  };

  return (
    <div className={cn('mt-4', !isInteractive && 'cursor-default', className)}>
      <div className="relative flex items-center content-stretch">
        <ol className="flex w-full items-center gap-[2px]">
          {stages.map((stage, index) => {
            const Icon = stage.icon;
            const isLast = index === stages.length - 1;
            const stateKey = getStageStateKey(
              activeStageKey,
              stage.stageKey,
              stages,
              variant
            );
            const state = shipmentProgressButtonStates[stateKey];
            const isDisabled = state.type === 'disabled';
            const isStageInteractive = isInteractive && interactiveStageKeys.includes(stage.stageKey) && !isDisabled;
            const isOutlined = variant === 'default' && stage.stageKey === selectedStageKey;

            return (
              <li
                className={cn(
                  itemClassNameByVariant[variant],
                  isLast ? 'shrink-0' : 'min-w-0 flex-1'
                )}
                key={stage.stageKey}
              >
                <button
                  aria-current={state.state === 'active' ? 'step' : undefined}
                  aria-label={stage.label}
                  className={getStageButtonClassName(isStageInteractive, isOutlined, state.type)}
                  data-stage-key={stage.stageKey}
                  data-stage-state={state.type}
                  disabled={!isStageInteractive}
                  onClick={isStageInteractive ? (event) => onClickStageButton(event, stage.stageKey) : undefined}
                  type="button"
                >
                  <span className={getStageTileClassName(isOutlined, stateKey, variant)}>
                    <Icon
                      aria-hidden
                      className={iconClassNameByVariant[variant]}
                      strokeWidth={2}
                    />
                  </span>
                </button>

                {!isLast && (
                  <span
                    aria-hidden
                    className={cn(
                      connectorBaseClassNameByVariant[variant],
                      connectorClassByToneByVariant[variant][state.connectorAfter]
                    )}
                  />
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
};

export default ShipmentProgressLine;